import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { brand } from "../../theme/themePrimitives";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useAuth } from "../../context/AuthContext";
import { useTranslate } from "../../hooks/useTranslate";

interface HttpErrorPageProps {
  status: 401 | 404;
  isNested?: boolean; // to differentiate between direct navigation and nested route error
}

export const HttpErrorPage: React.FC<HttpErrorPageProps> = ({
  status,
  isNested = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const t = useTranslate("errorPage");

  const isLoggedIn = !!auth?.user?.UserPortalUid;

  const statusConfig =
    status === 401
      ? {
          title: t("messages", "accessDeniedTitle"),
          subtitle: t("messages", "accessDeniedSubtitle"),
        }
      : {
          title: t("messages", "notFoundTitle"),
          subtitle: t("messages", "notFoundSubtitle"),
        };

  const message = (location.state as any)?.message;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: isNested ? "80vh" : "100vh",
        gap: "48px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "24px",
        }}
      >
        <Typography
          sx={{
            color: "#4543BF",
            fontSize: 16,
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {`${status} Error`}
        </Typography>
        <Typography
          sx={{
            fontSize: "60px",
            color: "#101828",
            fontWeight: "600",
            lineHeight: "72px",
          }}
        >
          {statusConfig.title}
        </Typography>
        <Typography
          sx={{
            color: "#475467",
            fontWeight: 400,
            fontSize: 20,
            lineHeight: "30px",
          }}
        >
          {statusConfig.subtitle}
        </Typography>

        {message && (
          <Stack
            flexDirection="row"
            sx={{
              backgroundColor: "#FFFBFA",
              border: "1px solid #FDA29B",
              borderRadius: "8px",
              p: "16px",
              width: "670px",
              gap: "12px",
            }}
          >
            <ErrorOutlineOutlinedIcon sx={{ color: "#B42318", mt: "2px" }} />
            <Stack gap="3px">
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#B42318",
                  lineHeight: "22px",
                }}
              >
                Backend Message:
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#B42318",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                {message}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>

      <Stack direction="row" gap="12px">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{
            px: "22px",
            py: "16px",
            minHeight: 56,
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            color: "#344054",
            fontWeight: "600",
            fontSize: "18px",
          }}
          onClick={() => navigate(-1)}
        >
          {t("messages", "goBack")}
        </Button>
        {isLoggedIn && (
          <Button
            component={Link}
            to="/home"
            replace
            sx={{
              px: "22px",
              py: "16px",
              minHeight: 56,
              borderRadius: "12px",
              backgroundColor: `${brand[600]}`,
              color: "white",
              fontWeight: "600",
              fontSize: "18px",
              "&:hover": {
                backgroundColor: `${brand[700]}`,
              },
            }}
          >
            {t("messages", "goToHomepage")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
